import { CC } from "@bigspring/core-components";
import { Company } from "@shared/Collections/models/company";
import { AppIcon, ExchangeIcon, MenuButton, Section } from "../../styles";
import { BaseMenu, MenuOption, Props as BaseMenuProps } from "../BaseMenu";
import { CompanySelector } from "../BaseMenu/styles";
import { CompanyName } from "./styles";
import { RevampedNavItem } from "../../menuOptions";
import { useEffect, useMemo, useState } from "react";

export type Props<Option extends MenuOption> = BaseMenuProps & {
  selectedCompany?: Company;
  options: Option[][];
  selectedOption?: Option & { uri?: string };
  companies: Company[];
  isNewAdminNav: boolean;
  revampedMenuOptions: RevampedNavItem[];
  onRevampedOptionClick: (option: RevampedNavItem) => Promise<void>;
  logoutClick: () => void;
  onOptionClick: (option: Option) => Promise<void>;
  onCompanyChange: (company: Company) => Promise<void>;
};

export function NonSuperAdminMenu<Option extends MenuOption>({
  label,
  options,
  selectedOption,
  selectedCompany,
  companies,
  isNewAdminNav,
  revampedMenuOptions,
  onRevampedOptionClick,
  logoutClick,
  onOptionClick,
  onOpenMenuClick,
  onCloseMenuClick,
  onCompanyChange,
}: Props<Option>) {
  // remove the super admin option
  const filteredMenuOptions = useMemo(() => {
    return revampedMenuOptions.filter((item) => item.text !== "Super Admin");
  }, [revampedMenuOptions]);

  const [accordionOpen, setAccordionOpen] = useState(1);

  const handleAccordionOpen = (panel: number) => {
    setAccordionOpen(accordionOpen === panel ? 0 : panel);
  };

  useEffect(() => {
    // find the index of the selected option in the revamped menu options
    const selectedOptionIndex = revampedMenuOptions.findIndex((option) =>
      option.subsections?.find(
        (subsection) => subsection.href === selectedOption?.uri
      )
    );
    setAccordionOpen(selectedOptionIndex + 1);
  }, [selectedOption, revampedMenuOptions]);

  if (isNewAdminNav) {
    return (
      <BaseMenu
        label={label}
        onOpenMenuClick={onOpenMenuClick}
        onCloseMenuClick={onCloseMenuClick}
        isNewAdminNav={isNewAdminNav}
      >
        <Section item container xs={12} direction="column" $borderBottom>
          {companies.length > 1 ? (
            <CC.FloatMenu
              color="primary"
              element={
                <CompanySelector
                  color="utility"
                  text={selectedCompany?.name}
                  endIcon={<ExchangeIcon />}
                  $isNewAdminNav={true}
                />
              }
              defaultValue={selectedCompany?.id}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              options={companies}
              onChange={onCompanyChange}
            />
          ) : (
            <CompanyName>{selectedCompany?.name}</CompanyName>
          )}
        </Section>

        <CC.Box mt={2}>
          {filteredMenuOptions.map((item, index) => {
            const contentHeight = (item.subsections?.length || 0) * 44;

            return (
              <CC.Box
                sx={{
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor:
                      accordionOpen === index + 1
                        ? "transparent"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                key={item.text}
                onClick={() => {
                  if (item.subsections?.length) {
                    handleAccordionOpen(index + 1);
                  } else {
                    void onRevampedOptionClick(item);
                  }
                }}
              >
                <CC.Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  height="44px"
                  marginLeft={"20px"}
                >
                  {item.icon || item.iconFilled || item.iconOutline}
                  {item.text}
                  <CC.Box
                    sx={{
                      marginLeft: "auto",
                      marginRight: "20px",
                      display: item.subsections?.length ? "block" : "none",
                    }}
                  >
                    {item.subsections?.length ? (
                      <CC.ChevronRightIcon
                        sx={{
                          transform:
                            accordionOpen === index + 1
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                          transition: "transform 0.4s ease",
                        }}
                      />
                    ) : null}
                  </CC.Box>
                </CC.Box>
                <CC.Box
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    transition: "max-height 0.4s ease",
                    maxHeight:
                      accordionOpen === index + 1
                        ? `${contentHeight}px`
                        : "0px",
                  }}
                >
                  {item.subsections?.map((subsection) => (
                    <CC.Box
                      key={subsection.text}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await onRevampedOptionClick(subsection);
                      }}
                      sx={{
                        height: "44px",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "52px",
                        color:
                          selectedOption?.uri === subsection.href
                            ? "white"
                            : "#6E6E6B",
                        backgroundColor:
                          selectedOption?.uri === subsection.href
                            ? "#E50063"
                            : "transparent",
                        "&:hover": {
                          cursor: "pointer",
                          backgroundColor: "#E50063",
                          color: "white",
                        },
                      }}
                    >
                      {subsection.text}
                    </CC.Box>
                  ))}
                </CC.Box>
              </CC.Box>
            );
          })}
        </CC.Box>
        {/* at the bottom of the menu */}
        <CC.Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            marginBottom: "10px",
          }}
        >
          <MenuButton
            text="App"
            color="admin-navigation"
            onClick={() =>
              void window.open("https://app.bigspring.io", "_blank")
            }
            startIcon={<AppIcon />}
          />
          <MenuButton
            text="Logout"
            color="admin-navigation"
            onClick={logoutClick}
            startIcon={<CC.LogoutIcon />}
          />
        </CC.Box>
      </BaseMenu>
    );
  }

  return (
    <BaseMenu
      label={label}
      onOpenMenuClick={onOpenMenuClick}
      onCloseMenuClick={onCloseMenuClick}
    >
      <CC.Grid item container xs={12} direction="column">
        <Section item container xs={12} direction="column" $borderBottom>
          {companies.length > 1 ? (
            <CC.FloatMenu
              element={
                <CompanySelector
                  color="utility"
                  text={selectedCompany?.name}
                  endIcon={<CC.ChevronDownIcon />}
                />
              }
              defaultValue={selectedCompany?.id}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              options={companies}
              onChange={onCompanyChange}
            />
          ) : (
            <CompanyName>{selectedCompany?.name}</CompanyName>
          )}
        </Section>
        {options.map((section, index) => (
          <Section
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            item
            xs={12}
            container
            direction="column"
            $borderBottom={index + 1 < options.length}
          >
            {section.map((option) => (
              <CC.Grid key={option.text} item>
                <MenuButton
                  text={option.text}
                  color="admin-navigation"
                  startIcon={option.startIcon(selectedOption === option)}
                  selected={selectedOption === option}
                  onClick={() => onOptionClick(option)}
                />
              </CC.Grid>
            ))}
          </Section>
        ))}
      </CC.Grid>
    </BaseMenu>
  );
}
